/**
 * Name tags on the web (docs/mentions.md, flag `mentionsUI`): the household's names and each peon's
 * tag colour, read once a page load from GET /api/peons (its `shirts`) and worked out for the whole
 * household at once (no two peons share one), for MentionText and
 * MentionField. Null while the flag is off or before the first read, and then every text draws plain.
 * The rules (what is a tag, the colour, the picker) are shared/mentions.ts.
 */
import { useEffect, useState } from "preact/hooks";
import { api } from "./api";
import { useMentionsUI } from "./features";
import { mentionColour, mentionColours, shirtOf, shirtSwatch, type MentionColour } from "../shared/mentions.ts";

export interface MentionRoster {
  names: string[];
  colourOf: (name: string) => MentionColour;
}

let roster: MentionRoster | null = null;
let reading: Promise<void> | null = null;
const listeners = new Set<(r: MentionRoster | null) => void>();

function rosterOf(names: string[], shirts: Record<string, string> | undefined): MentionRoster {
  // The whole household's colours at once, so no two peons share one (shared/mentions.ts mentionColours).
  const colours = mentionColours(names, shirts);
  return {
    names,
    colourOf: (name) => colours[name.trim().toLowerCase()] ?? mentionColour(shirtSwatch(shirtOf(shirts, name))),
  };
}

function read(): void {
  if (reading) return;
  reading = api.peons.list().then(
    ({ names, shirts }) => {
      roster = rosterOf(names, shirts);
      for (const l of listeners) l(roster);
    },
    () => {
      reading = null; // offline: try again next time a tagged text mounts
    },
  );
}

/** Tests only: a roster as if read, or none. */
export function setMentionRosterForTests(names: string[] | null, shirts?: Record<string, string>): void {
  roster = names ? rosterOf(names, shirts) : null;
  reading = names ? Promise.resolve() : null;
  for (const l of listeners) l(roster);
}

/** The roster while `mentionsUI` is on (read on first use), else null. */
export function useMentionRoster(): MentionRoster | null {
  const on = useMentionsUI();
  const [r, setR] = useState<MentionRoster | null>(roster);
  useEffect(() => {
    if (!on) return;
    listeners.add(setR);
    if (roster) setR(roster);
    else read();
    return () => {
      listeners.delete(setR);
    };
  }, [on]);
  return on ? r : null;
}
